import React, { useEffect, useRef, useState } from 'react';
import { getMapData, show3dMap } from '@mappedin/mappedin-js';
import { BlueDot } from '@mappedin/blue-dot';
import '@mappedin/mappedin-js/lib/index.css';

const MappedInMapSimple = ({ spaceName, brandColor, onMapReady, videoDuration = 0 }) => {
  const mapContainerRef = useRef(null);
  const mapViewRef = useRef(null);
  const mapDataRef = useRef(null);
  const blueDotRef = useRef(null);
  const initializingRef = useRef(false);

  const animationRef = useRef({
    path: [],
    directions: null,
    index: 0,
    interval: null,
    isPlaying: false,
    stepDuration: 300,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [error, setError] = useState(null);
  const [pathInfo, setPathInfo] = useState({
    totalSteps: 0,
    markerCount: 0,
    stepDuration: 0,
    navigationDuration: 0,
  });

  const MAPPEDIN_CONFIG = {
    key: 'mik_gxWE7PUdHxoK9qNlr555107ac',
    secret: 'mis_hvx40sGJZ2cuyzSDEHv55PtldjuszH20dVqhRkBV8R597271073',
    mapId: '69709560a8a681000bf10352',
  };

  const START_DELAY = 5;
  const END_BUFFER = 6;

  useEffect(() => {
    if (initializingRef.current) return;

    const initializeMap = async () => {
      if (!mapContainerRef.current) {
        console.log('[MAP] Container ref not ready, waiting...');
        return;
      }

      initializingRef.current = true;

      try {
        console.log('[MAP] Starting initialization...');

        const data = await getMapData(MAPPEDIN_CONFIG);
        console.log('[MAP] Data loaded');

        const view = await show3dMap(mapContainerRef.current, data);
        console.log('[MAP] View created');

        mapDataRef.current = data;
        mapViewRef.current = view;

        // Give the map a moment to fully render
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Add labels carefully
        try {
          const spaces = data.getByType('space');
          console.log('[MAP] Adding labels for', spaces.length, 'spaces');

          for (const space of spaces) {
            if (space && space.name) {
              try {
                view.Labels.add(space, space.name);
              } catch (labelError) {
                console.warn('[MAP] Could not add label for', space.name);
              }
            }
          }
        } catch (labelError) {
          console.warn('[MAP] Label error:', labelError);
        }

        // Initialize BlueDot
        try {
          const dot = new BlueDot(view);
          dot.enable({
            radius: 10,
            showPersonIcon: false,
            color: '#2196F3',
            animateMovement: false,
            showAccuracyRadius: false,
          });
          dot.follow('position-only');
          blueDotRef.current = dot;
          console.log('[MAP] BlueDot ready');
        } catch (dotError) {
          console.error('[MAP] BlueDot error:', dotError);
        }

        // Prepare route
        await prepareRoute(spaceName, data, view);

        setIsLoading(false);
        setIsMapReady(true);

        if (onMapReady) {
          onMapReady();
        }

        console.log('[MAP] ✅ Initialization complete');
      } catch (err) {
        console.error('[MAP] Initialization error:', err);
        setError(err.message || 'Failed to load map');
        setIsLoading(false);
        initializingRef.current = false;
      }
    };

    const timer = setTimeout(initializeMap, 200);
    return () => clearTimeout(timer);
  }, [spaceName]);

  const prepareRoute = async (targetSpaceName, data, view) => {
    try {
      const spaces = data.getByType('space');
      const start = spaces.find(s => s.name === 'Kiosk');
      const end = spaces.find(
        s => s.name && s.name.toLowerCase().trim() === targetSpaceName.toLowerCase().trim()
      );

      if (!start || !end) {
        throw new Error(`Could not find start (Kiosk) or end (${targetSpaceName})`);
      }

      console.log('[MAP] Getting directions from', start.name, 'to', end.name);

      const directions = await view.getDirections(start, end, { smoothing: true });

      if (!directions || !directions.coordinates) {
        throw new Error('No directions found');
      }

      console.log('[MAP] Route has', directions.coordinates.length, 'coordinates');

      // Draw path
      view.Navigation.draw(directions);

      // Add markers
      for (let i = 0; i < directions.instructions.length; i++) {
        const instruction = directions.instructions[i];
        const isLast = i === directions.instructions.length - 1;

        const markerHTML = `
          <div style="
            background: white;
            border: 2px solid ${brandColor || '#127E10'};
            border-radius: 8px;
            padding: 8px 12px;
            font-size: 12px;
            font-weight: bold;
            color: #333;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            max-width: 200px;
            text-align: center;
          ">
            ${isLast ? '🎯 Arrived!' : `${instruction.action.type}`}
          </div>
        `;

        try {
          view.Markers.add(instruction.coordinate, markerHTML, { rank: 'always-visible' });
        } catch (markerError) {
          console.warn('[MAP] Could not add marker', i);
        }
      }

      // Position BlueDot at start
      if (blueDotRef.current) {
        const startCoord = directions.coordinates[0];
        blueDotRef.current.update({
          latitude: startCoord.latitude,
          longitude: startCoord.longitude,
          floorOrFloorId: startCoord.floor,
          accuracy: 1,
          timestamp: Date.now(),
        });
      }

      // Store path
      animationRef.current.path = directions.coordinates;
      animationRef.current.directions = directions;

      setPathInfo({
        totalSteps: directions.coordinates.length,
        markerCount: directions.instructions.length,
        stepDuration: 0,
        navigationDuration: 0,
      });

      // Focus camera
      view.Camera.focusOn(directions.coordinates.slice(0, 5), {
        screenOffsets: { bottom: 50, left: 50, top: 50, right: 50 },
      });

      console.log('[MAP] Route prepared successfully');
    } catch (err) {
      console.error('[MAP] Route preparation error:', err);
      throw err;
    }
  };

  // Calculate timing
  useEffect(() => {
    if (videoDuration > 0 && pathInfo.totalSteps > 0) {
      const navigationDuration = Math.max(videoDuration - START_DELAY - END_BUFFER, 5);
      const stepDuration = (navigationDuration * 1000) / pathInfo.totalSteps;
      const finalStepDuration = Math.max(100, Math.min(1000, stepDuration));

      animationRef.current.stepDuration = finalStepDuration;

      setPathInfo(prev => ({
        ...prev,
        stepDuration: finalStepDuration,
        navigationDuration: navigationDuration,
      }));

      console.log(
        '[TIMING] Video:',
        videoDuration,
        's | Nav:',
        navigationDuration,
        's | Step:',
        finalStepDuration,
        'ms'
      );
    }
  }, [videoDuration, pathInfo.totalSteps]);

  const playNavigation = () => {
    if (animationRef.current.isPlaying) return;
    if (!blueDotRef.current || !mapViewRef.current || !animationRef.current.path.length) return;

    console.log('[NAV] Starting...');
    animationRef.current.isPlaying = true;

    setTimeout(() => {
      if (!animationRef.current.isPlaying) return;

      animationRef.current.interval = setInterval(() => {
        const { path, index } = animationRef.current;

        if (index >= path.length) {
          stopNavigation();
          return;
        }

        const coord = path[index];

        blueDotRef.current.update({
          latitude: coord.latitude,
          longitude: coord.longitude,
          floorOrFloorId: coord.floor,
          accuracy: 1,
          timestamp: Date.now(),
        });

        if (mapViewRef.current && mapViewRef.current.Navigation) {
          try {
            mapViewRef.current.Navigation.highlightPathSection(path[0], coord, {
              animationDuration: 0,
              color: '#9d9d9d',
              widthMultiplier: 1.1,
            });
          } catch (err) {
            // Ignore highlight errors
          }
        }

        animationRef.current.index++;
      }, animationRef.current.stepDuration);
    }, START_DELAY * 1000);
  };

  const pauseNavigation = () => {
    if (!animationRef.current.isPlaying) return;
    clearInterval(animationRef.current.interval);
    animationRef.current.isPlaying = false;
    console.log('[NAV] Paused');
  };

  const stopNavigation = () => {
    clearInterval(animationRef.current.interval);
    animationRef.current.index = 0;
    animationRef.current.isPlaying = false;

    if (blueDotRef.current && animationRef.current.path.length > 0) {
      const startCoord = animationRef.current.path[0];
      blueDotRef.current.update({
        latitude: startCoord.latitude,
        longitude: startCoord.longitude,
        floorOrFloorId: startCoord.floor,
        accuracy: 1,
        timestamp: Date.now(),
      });
    }

    if (mapViewRef.current && animationRef.current.directions) {
      try {
        mapViewRef.current.Navigation.clear();
        mapViewRef.current.Navigation.draw(animationRef.current.directions);
      } catch (err) {
        // Ignore redraw errors
      }
    }

    console.log('[NAV] Stopped');
  };

  // Video sync
  useEffect(() => {
    if (!isMapReady) return;

    const setupSync = () => {
      const video = document.getElementById('avatarVideo');
      if (!video) return false;

      const handlePlay = () => playNavigation();
      const handlePause = () => pauseNavigation();
      const handleEnded = () => stopNavigation();
      const handleSeeked = () => stopNavigation();

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);
      video.addEventListener('seeked', handleSeeked);

      console.log('[SYNC] Video listeners attached');

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
        video.removeEventListener('seeked', handleSeeked);
      };
    };

    const cleanup = setupSync();
    if (cleanup) return cleanup;

    const interval = setInterval(() => {
      const cleanup = setupSync();
      if (cleanup) {
        clearInterval(interval);
        return cleanup;
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isMapReady]);

  if (isLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
        }}
      >
        <div
          style={{
            width: '50px',
            height: '50px',
            border: '5px solid #127E10',
            borderTop: '5px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ marginTop: '20px', fontSize: '16px' }}>Loading Map...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          padding: '20px',
        }}
      >
        <div style={{ fontSize: '48px' }}>⚠️</div>
        <p style={{ color: '#d32f2f', fontWeight: 'bold', marginTop: '10px' }}>Map Error</p>
        <p style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>{error}</p>
        <button
          onClick={() => {
            setError(null);
            setIsLoading(true);
            initializingRef.current = false;
          }}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#127E10',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div
        ref={mapContainerRef}
        style={{
          width: '100%',
          height: '100%',
          minHeight: '500px',
          borderRadius: '15px',
          overflow: 'hidden',
        }}
      />

      {pathInfo.navigationDuration > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '15px',
            left: '15px',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '12px',
            zIndex: 999,
          }}
        >
          <div>
            <strong>📊 Timing</strong>
          </div>
          <div>Start: {START_DELAY}s</div>
          <div>Nav: {pathInfo.navigationDuration.toFixed(1)}s</div>
          <div>Steps: {pathInfo.totalSteps}</div>
          <div>Step: {pathInfo.stepDuration.toFixed(0)}ms</div>
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          top: '15px',
          right: '15px',
          backgroundColor: animationRef.current.isPlaying ? '#4CAF50' : '#FF9800',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 'bold',
          zIndex: 999,
        }}
      >
        {animationRef.current.isPlaying ? '🧭 Navigating' : '⏸️ Ready'}
      </div>
    </div>
  );
};

export default MappedInMapSimple;
